import { isBannerAdObject } from "../ads/adrequest";
import Service from "../service";
import { getTagsByLineitem, matchRoadblocks } from "./roadblock";
import "intersection-observer";
class LazyloadService extends Service {
    constructor(config, context) {
        super("Lazyload", context);
        this.lazyloadSettings = new Map();
        this.roadblocks = [];
        this.targets = new Map();
        if (!config) {
            throw new Error("Missing lazyload configuration");
        }
        this.configuration = config;
    }
    async initLazyloader() {
        this.resetLazyloader();
    }
    setLazyloadOptions(divId, options) {
        this.logger.logInfo(`Set lazyload opts for ${divId}: ${JSON.stringify(options, null, 2)}`);
        this.lazyloadSettings.set(divId, {
            offset: {
                top: this.configuration.defaults.offset.top,
                bottom: this.configuration.defaults.offset.bottom,
            },
            ...options,
        });
    }
    setRoadblocks(roadblocks, adResponses) {
        const roadblockLineitems = getTagsByLineitem(adResponses);
        const matchedRoadblocks = matchRoadblocks(roadblocks, roadblockLineitems);
        this.logger.logInfo(`Found ${matchedRoadblocks.length} roadblocks`);
        this.roadblocks = matchedRoadblocks;
    }
    lazyloadPlacement(divId, callback, adObject) {
        this.logger.logInfo(`Lazyload requested for ${divId}`);
        const placementSettings = this.lazyloadSettings.get(divId);
        const secondaries = this.roadblocks.map((rb) => rb.secondaries).flat();
        if (secondaries.includes(divId)) {
            this.logger.logInfo(`Tag ${divId} is a roadblock secondary, skipping observer`);
            this.targets.set(divId, { callback });
            return;
        }
        if (this.configuration.enabled && placementSettings?.disable !== true) {
            const top = placementSettings?.offset?.top ??
                this.configuration.defaults.offset.top;
            const bottom = placementSettings?.offset?.bottom ??
                this.configuration.defaults.offset.bottom;
            const options = {
                top: typeof top === "number" ? `${top.toString(10)}px` : top,
                bottom: typeof bottom === "number" ? `${bottom.toString(10)}px` : bottom,
            };
            this.createObserver(divId, callback, options, adObject);
        }
        else {
            callback();
        }
    }
    resetLazyloader() {
        this.logger.logInfo(`Resetting lazyloader`);
        this.targets.forEach((w) => w.observer?.disconnect());
        this.targets.clear();
        this.lazyloadSettings.clear();
        this.roadblocks.length = 0;
    }
    activateObserver(divId) {
        const e = window.document.getElementById(divId);
        if (e) {
            const targetObserver = this.targets.get(divId)?.observer;
            if (targetObserver) {
                try {
                    const displayV = window
                        .getComputedStyle(e)
                        .getPropertyValue("display");
                    if (displayV === "none") {
                        this.logger.logInfo(`Overwriting display: none for ${divId} - IO can't detect these`);
                        e.style.display = "inherit";
                    }
                }
                catch (error) {
                    this.logger.logWarn(`Error occured while overwriting css prop: ${error}`);
                }
                this.logger.logInfo(`Activating observer for tag ${divId}`);
                targetObserver.observe(e);
            }
        }
        else {
            this.logger.logError(`Could not find element ${divId} to observe`);
        }
    }
    activateAllObservers() {
        Array.from(this.targets.keys()).forEach((divId) => this.activateObserver(divId));
    }
    removeObserver(divId) {
        this.logger.logInfo(`Removing observer for ${divId}`);
        const targetObserver = this.targets.get(divId)?.observer;
        if (targetObserver) {
            targetObserver.disconnect();
        }
        this.targets.delete(divId);
    }
    createObserver(divId, callback, options, adObject) {
        // Setup according to config
        let rootMargin = "0px";
        let bottom = 0;
        let threshold = 0;
        // Get height from a banner ad object, otherwise it is native or video. Native we can only guess and for video we'd have to parse the VAST
        // 150 pixels might be a good starting point
        const height = adObject && isBannerAdObject(adObject) ? adObject.banner.height : 150;
        if (options?.top.startsWith("-")) {
            threshold = 1;
            // Figure out whether we use pxs or %S
            if (options.top.endsWith("px")) {
                // Count ratio as a relative height to ad
                bottom = Number.parseInt(options.top.slice(0, -2), 10);
            }
            else if (options.top.endsWith("%")) {
                // Set % directly as threshold
                bottom =
                    (-1 *
                        (Math.abs(Number.parseInt(options.top.slice(0, -1), 10)) *
                            height)) /
                        100;
            }
            rootMargin = `0px 0px ${bottom}px 0px`;
        }
        else {
            rootMargin = `${options?.bottom ?? "0px"} 0px ${options?.top ?? "0px"} 0px`;
        }
        this.logger.logInfo(`Calculated rootMargin ${rootMargin} and threshold ${threshold} to for this observer`);
        const renderAdCallback = () => {
            this.logger.logInfo(`Callback for ${divId}`);
            // For roadblocks
            if (this.roadblocks.length > 0) {
                this.roadblocks.forEach((rb) => {
                    if (rb.primary === divId) {
                        this.logger.logInfo(`Triggering secondaries ${rb.secondaries.join(", ")} for ${divId}`);
                        rb.secondaries.forEach((s) => {
                            const target = this.targets.get(s);
                            if (target) {
                                target.callback();
                            }
                        });
                    }
                });
            }
            // For non roadblock cases
            callback();
        };
        const observerCallback = (entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    this.logger.logInfo(`Lazyload triggered for ${divId}`);
                    renderAdCallback();
                    this.removeObserver(divId);
                }
            });
        };
        if ("IntersectionObserver" in window) {
            const observer = new IntersectionObserver(observerCallback, {
                root: null,
                rootMargin,
                threshold,
            });
            this.targets.set(divId, { observer, callback });
            this.logger.logInfo(`Observer created for ${divId}`);
        }
        else {
            this.logger.logError(`Cannot find IO in window - to be safe, skip lazy`);
            renderAdCallback();
        }
    }
}
export default LazyloadService;
//# sourceMappingURL=lazyload.js.map